/**
 * Pure rules for the Pipelines list and the pipeline wizard: the delay in
 * days, hours, minutes and seconds; receiver types and the receiver an
 * existing event type fixes; Meta's approval status of a WhatsApp template;
 * who a comm sends as; and reading the list rows and the payload answer.
 */
import {
    DEFAULT_SENDER_EMAIL,
    receiverTypes,
    WHATSAPP_ACCOUNTS,
} from './Constants'

// ── Delay ───────────────────────────────────────────────────────────────

/**
 * The units of a pipeline's delay (`schedule`, stored in seconds), largest
 * first. `max` bounds a field so every total has one way to be typed: the old
 * app took hours, minutes and seconds from a 24-hour time picker.
 */
export const DELAY_UNITS = [
    {
        key: 'days',
        label: 'Days',
        one: 'day',
        many: 'days',
        seconds: 86400,
        max: null,
    },
    {
        key: 'hours',
        label: 'Hours',
        one: 'hour',
        many: 'hours',
        seconds: 3600,
        max: 23,
    },
    {
        key: 'minutes',
        label: 'Minutes',
        one: 'minute',
        many: 'minutes',
        seconds: 60,
        max: 59,
    },
    {
        key: 'seconds',
        label: 'Seconds',
        one: 'second',
        many: 'seconds',
        seconds: 1,
        max: 59,
    },
]

/**
 * A count typed in a delay field as a whole, non-negative number. Empty,
 * negative and non-numeric input count as 0: the old app saved `null` when
 * the days box was emptied (`parseInt('')`).
 */
const wholeCount = (value) => {
    const n = Math.floor(Number(value))
    return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * Split a delay in seconds into days, hours, minutes and seconds.
 *
 * @param {number} total  seconds; anything not a positive number is 0
 * @returns {{days: number, hours: number, minutes: number, seconds: number}}
 */
export const secondsToParts = (total) => {
    let rest = wholeCount(total)
    return DELAY_UNITS.reduce((parts, unit) => {
        parts[unit.key] = Math.floor(rest / unit.seconds)
        rest -= parts[unit.key] * unit.seconds
        return parts
    }, {})
}

/**
 * The delay in seconds for the four counts. A missing or empty count is 0.
 *
 * @param {{days?, hours?, minutes?, seconds?}} parts
 * @returns {number}
 */
export const partsToSeconds = (parts = {}) =>
    DELAY_UNITS.reduce(
        (sum, unit) => sum + wholeCount(parts?.[unit.key]) * unit.seconds,
        0
    )

/**
 * The count a delay field keeps for what was typed: a whole number, at most
 * the unit's maximum (23 hours, 59 minutes, 59 seconds; days are unbounded).
 */
export const clampDelayPart = (unitKey, value) => {
    const n = wholeCount(value)
    const max = DELAY_UNITS.find((u) => u.key === unitKey)?.max
    return max == null ? n : Math.min(n, max)
}

/**
 * A delay in words, largest unit first: "2 days 3 hours", "1 minute 5
 * seconds", "No delay" for 0. The review showed "Delayed by 172800s".
 */
export const describeDelay = (total) => {
    const parts = secondsToParts(total)
    const words = DELAY_UNITS.filter((u) => parts[u.key] > 0).map(
        (u) => `${parts[u.key]} ${parts[u.key] === 1 ? u.one : u.many}`
    )
    return words.length ? words.join(' ') : 'No delay'
}

// ── Receiver types ──────────────────────────────────────────────────────

const KNOWN_RECEIVER_LABELS = Object.fromEntries(
    receiverTypes.map((r) => [r.value, r.label])
)

// Only these audiences are offered for a new pipeline. A saved pipeline with
// another receiver type still shows it (see `current` below).
const OFFERED_RECEIVER_TYPES = ['batch_student', '__inline']

/**
 * The receiver types to offer: the service's own list from GET
 * /comms/v2/pipeline/create/enums (`enums.receiver_types`), else the
 * hardcoded fallback. Known values keep the app's labels ("Demo Student",
 * not the service's "Demo student"). A `current` value missing from the list
 * (an unknown receiver on a saved pipeline) is added, so the select never
 * shows empty for it.
 *
 * @returns {{value: string, label: string}[]}
 */
export const receiverTypeOptions = (enums, current = '') => {
    const fromApi = Array.isArray(enums?.receiver_types)
        ? enums.receiver_types.filter((r) => r && r.value)
        : []
    const source = fromApi.length ? fromApi : receiverTypes
    const options = source
        .filter((r) => OFFERED_RECEIVER_TYPES.includes(r.value))
        .map((r) => ({
            value: r.value,
            label: KNOWN_RECEIVER_LABELS[r.value] ?? r.label ?? r.value,
        }))
    if (current && !options.some((o) => o.value === current))
        options.push({
            value: current,
            label: KNOWN_RECEIVER_LABELS[current] ?? current,
        })
    return options
}

/** The label of a receiver type among `options`, else the value itself. */
export const receiverLabel = (options, value) =>
    options.find((o) => o.value === value)?.label ?? value ?? ''

/**
 * The receiver type an existing event type fixes, or '' for a new (or
 * unknown) event type.
 *
 * Identities are resolved per receiver type, so a second pipeline on the
 * same event with another receiver type would receive identities it cannot
 * use. The old app preset the receiver from the event type's existing
 * pipeline and locked the select; the enums carry it as
 * `event_types[].dependencies.receiver_types`.
 */
export const receiverPreset = (enums, eventType) => {
    if (!eventType || !Array.isArray(enums?.event_types)) return ''
    const entry = enums.event_types.find((e) => e?.value === eventType)
    return (
        entry?.dependencies?.receiver_types?.find((r) => r && r.value)?.value ??
        ''
    )
}

// ── Templates: Meta status and who a comm sends as ─────────────────────

// Meta will not send a template in these states, so it is not offered.
const META_BLOCKED = [
    'archived',
    'rejected',
    'disabled',
    'paused',
    'deleted',
    'pending_deletion',
]
// Not approved yet: offered, with a warning that sends fail until it is.
const META_WAITING = ['pending', 'in_appeal']

/**
 * Meta's approval status of a WhatsApp template (`external_template_status`,
 * in either case), or null for any other channel.
 *
 * `tone`: `ok` (approved), `waiting` (pending, in appeal), `blocked` (Meta
 * will not send it), `none` (no status recorded, or one this app does not
 * know). `sendable` is false only for blocked. Every production WhatsApp
 * template is internally "approved", so Meta's status is the one that says
 * whether it can be sent (550 are archived).
 */
export const metaStatus = (template) => {
    if (template?.comm_type !== 'whatsapp') return null
    const key = String(template.external_template_status ?? '')
        .trim()
        .toLowerCase()
    if (!key)
        return {
            key: 'none',
            label: 'No Meta status',
            tone: 'none',
            sendable: true,
        }
    const label = `Meta: ${key.replace(/_/g, ' ')}`
    if (key === 'approved') return { key, label, tone: 'ok', sendable: true }
    if (META_WAITING.includes(key))
        return { key, label, tone: 'waiting', sendable: true }
    if (META_BLOCKED.includes(key))
        return { key, label, tone: 'blocked', sendable: false }
    return { key, label, tone: 'none', sendable: true }
}

/**
 * Templates a channel can be assigned: same channel, internally approved
 * (the sender only loads approved templates), not refused by Meta for
 * WhatsApp, and matching the search. The template already assigned is always
 * kept, so an edit never silently drops it (the card flags it instead).
 */
export const assignableTemplates = (templates, commType, query, assignedId) => {
    const needle = String(query ?? '')
        .trim()
        .toLowerCase()
    return (templates ?? []).filter(
        (t) =>
            t.comm_type === commType &&
            ((t.status === 'approved' && metaStatus(t)?.sendable !== false) ||
                t.id === assignedId) &&
            (!needle || t.template_name?.toLowerCase().includes(needle))
    )
}

/** The name of a WhatsApp account (a template's `channel`; none is `default`). */
export const whatsappAccountLabel = (channel) => {
    const value = channel || 'default'
    return WHATSAPP_ACCOUNTS.find((a) => a.value === value)?.label ?? value
}

/** The From address of an email template (the sender's default when it has none). */
export const emailSender = (template) =>
    template?.source_email || DEFAULT_SENDER_EMAIL

/**
 * One line naming who a comm sends as, or '' for channels with no choice:
 * the WhatsApp account (`channel`, from the template on assign) or the email
 * template's From address.
 */
export const sendsAs = (commType, { channel, template } = {}) => {
    if (commType === 'whatsapp')
        return `Sends from ${whatsappAccountLabel(
            channel ?? template?.channel
        )}`
    if (commType === 'email') return `From ${emailSender(template)}`
    return ''
}

// ── List rows ───────────────────────────────────────────────────────────

/** A pipeline's first comm: legacy rows store `comms` as one object, newer ones as a list. */
export const pipelineComm = (pipeline) => {
    const comms = pipeline?.comms
    return (Array.isArray(comms) ? comms[0] : comms) ?? {}
}

/** The template a pipeline sends. */
export const pipelineTemplateId = (pipeline) =>
    pipelineComm(pipeline).template_id ?? pipeline?.template_id ?? ''

/**
 * The name of the template a pipeline sends. The unfiltered list attaches
 * the template (`pipeline.template`); one event type's list does not, so the
 * id is looked up in the loaded templates.
 */
export const pipelineTemplateName = (pipeline, templates = []) => {
    if (pipeline?.template?.template_name)
        return pipeline.template.template_name
    const id = pipelineTemplateId(pipeline)
    return (id && templates.find((t) => t.id === id)?.template_name) || ''
}

/** Rows whose name contains `query`, ignoring case; every row for an empty query. */
export const filterPipelinesByName = (rows, query) => {
    const needle = String(query ?? '')
        .trim()
        .toLowerCase()
    if (!needle) return rows ?? []
    return (rows ?? []).filter((p) =>
        String(p?.pipeline_name ?? '')
            .toLowerCase()
            .includes(needle)
    )
}

// ── Integration payload ─────────────────────────────────────────────────

/**
 * Read GET /comms/v2/get_payload. Success is `{status: 'SUCCESS', payload}`.
 * With no active pipeline on the event type the service answers 200 with
 * `{status: 'FAILURE', message: 'Payload cannot be created'}`, which the old
 * dialog showed as an empty box.
 *
 * @returns {{payload: object}|{error: string}}
 */
export const payloadResult = (body) => {
    if (
        body?.status === 'SUCCESS' &&
        body.payload &&
        typeof body.payload === 'object'
    )
        return { payload: body.payload }
    return {
        error:
            (typeof body?.message === 'string' && body.message) ||
            'The payload could not be built.',
    }
}
